import React, { useState } from 'react';
import { useToast } from '../hooks/useToast';
import { useRolesActivos } from '../hooks/useRoles';
import {
  useUsuarios,
  useCrearUsuario,
  useActualizarUsuario,
  useEliminarUsuario,
  useResetearPassword,
} from '../hooks/useUsuarios';
import PageHeader from '../components/PageHeader';
import {
  Users,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Key,
  X,
  Shield,
  Mail,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import type { Usuario } from '@suminuta/shared';

// Tipo para usuario sin password
type UsuarioSinPassword = Omit<Usuario, 'password_hash'>;

interface GestionUsuariosProps {
  embebido?: boolean;
}

const GestionUsuarios: React.FC<GestionUsuariosProps> = ({
  embebido = false,
}) => {
  const { success, error: showError } = useToast();

  // Estados de filtros y UI
  const [filtros, setFiltros] = useState({
    page: 1,
    limit: 10,
    busqueda: '',
    rol_id: undefined as number | undefined,
    activo: undefined as boolean | undefined,
  });
  const [showFiltros, setShowFiltros] = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [modoModal, setModoModal] = useState<'crear' | 'editar' | 'resetear'>(
    'crear'
  );
  const [usuarioSeleccionado, setUsuarioSeleccionado] =
    useState<UsuarioSinPassword | null>(null);

  // Hooks de datos
  const { data: roles } = useRolesActivos();
  const { data: usuariosData, isLoading } = useUsuarios({
    page: filtros.page,
    limit: filtros.limit,
    search: filtros.busqueda || undefined,
    activo: filtros.activo,
  });

  // Mutations
  const crearMutation = useCrearUsuario();
  const actualizarMutation = useActualizarUsuario();
  const eliminarMutation = useEliminarUsuario();
  const resetearPasswordMutation = useResetearPassword();

  const usuarios = usuariosData?.data || [];
  const pagination = usuariosData?.pagination;

  // Handlers
  const handleCrearUsuario = () => {
    setUsuarioSeleccionado(null);
    setModoModal('crear');
    setMostrarModal(true);
  };

  const handleEditarUsuario = (usuario: UsuarioSinPassword) => {
    setUsuarioSeleccionado(usuario);
    setModoModal('editar');
    setMostrarModal(true);
  };

  const handleResetearPassword = (usuario: UsuarioSinPassword) => {
    setUsuarioSeleccionado(usuario);
    setModoModal('resetear');
    setMostrarModal(true);
  };

  const handleEliminarUsuario = async (usuario: UsuarioSinPassword) => {
    if (
      !window.confirm(
        `¿Está seguro de eliminar al usuario "${usuario.nombre}"?`
      )
    ) {
      return;
    }

    try {
      await eliminarMutation.mutateAsync(usuario.id);
      success('Usuario eliminado correctamente');
    } catch (error) {
      showError('Error al eliminar usuario');
    }
  };

  const handleSubmitModal = async (data: any) => {
    try {
      if (modoModal === 'crear') {
        await crearMutation.mutateAsync(data);
        success('Usuario creado correctamente');
      } else if (modoModal === 'editar') {
        await actualizarMutation.mutateAsync({
          id: usuarioSeleccionado!.id,
          data,
        });
        success('Usuario actualizado correctamente');
      } else if (modoModal === 'resetear') {
        await resetearPasswordMutation.mutateAsync({
          usuario_id: usuarioSeleccionado!.id,
          nueva_password: data.password,
        });
        success('Contraseña reseteada correctamente');
      }
      setMostrarModal(false);
    } catch (error) {
      showError(
        `Error al ${modoModal === 'crear' ? 'crear' : 'actualizar'} usuario`
      );
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      setFiltros((prev) => ({ ...prev, page: newPage }));
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      page: 1,
      limit: 10,
      busqueda: '',
      rol_id: undefined,
      activo: undefined,
    });
  };

  return (
    <div className={embebido ? '' : 'min-h-screen bg-gray-50 dark:bg-gray-900'}>
      {!embebido && (
        <PageHeader
          title="Gestión de Usuarios"
          subtitle="Administrar usuarios del sistema y asignar roles"
        />
      )}

      <div
        className={
          embebido ? 'p-6' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'
        }
      >
        {/* Barra de acciones */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Buscador */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o email..."
                  value={filtros.busqueda}
                  onChange={(e) =>
                    setFiltros((prev) => ({
                      ...prev,
                      busqueda: e.target.value,
                      page: 1,
                    }))
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowFiltros(!showFiltros)}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <Filter className="w-5 h-5" />
                Filtros
              </button>
              <button
                onClick={handleCrearUsuario}
                className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Nuevo Usuario
              </button>
            </div>
          </div>

          {/* Panel de filtros expandible */}
          {showFiltros && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Filtro por rol */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol
                  </label>
                  <select
                    value={filtros.rol_id || ''}
                    onChange={(e) =>
                      setFiltros((prev) => ({
                        ...prev,
                        rol_id: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                        page: 1,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todos los roles</option>
                    {roles?.map((rol) => (
                      <option key={rol.id} value={rol.id}>
                        {rol.nombre_display}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Filtro por estado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <select
                    value={
                      filtros.activo === undefined
                        ? ''
                        : filtros.activo
                          ? 'true'
                          : 'false'
                    }
                    onChange={(e) =>
                      setFiltros((prev) => ({
                        ...prev,
                        activo:
                          e.target.value === ''
                            ? undefined
                            : e.target.value === 'true',
                        page: 1,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todos</option>
                    <option value="true">Activos</option>
                    <option value="false">Inactivos</option>
                  </select>
                </div>

                {/* Botón limpiar filtros */}
                <div className="flex items-end">
                  <button
                    onClick={limpiarFiltros}
                    className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Limpiar filtros
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabla de usuarios */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
          ) : usuarios.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                No hay usuarios
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Comienza creando un nuevo usuario.
              </p>
              <div className="mt-6">
                <button
                  onClick={handleCrearUsuario}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Nuevo Usuario
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Rol
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {usuarios.map((usuario) => (
                      <tr
                        key={usuario.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
                              <span className="text-blue-600 dark:text-blue-300 font-semibold">
                                {usuario.nombre.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {usuario.nombre}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <Mail className="w-4 h-4 mr-2" />
                            {usuario.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300">
                            <Shield className="w-3 h-3 mr-1" />
                            {roles?.find((r) => r.id === usuario.rol_id)
                              ?.nombre_display || 'Sin rol'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {usuario.activo ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300">
                              <UserCheck className="w-3 h-3 mr-1" />
                              Activo
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300">
                              <UserX className="w-3 h-3 mr-1" />
                              Inactivo
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditarUsuario(usuario)}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded"
                              title="Editar usuario"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleResetearPassword(usuario)}
                              className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 p-1 hover:bg-yellow-50 dark:hover:bg-yellow-900/50 rounded"
                              title="Resetear contraseña"
                            >
                              <Key className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEliminarUsuario(usuario)}
                              className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                              title="Eliminar usuario"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {pagination && pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Mostrando{' '}
                          <span className="font-medium">
                            {(pagination.page - 1) * pagination.limit + 1}
                          </span>{' '}
                          a{' '}
                          <span className="font-medium">
                            {Math.min(
                              pagination.page * pagination.limit,
                              pagination.total
                            )}
                          </span>{' '}
                          de{' '}
                          <span className="font-medium">
                            {pagination.total}
                          </span>{' '}
                          resultados
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <button
                            onClick={() =>
                              handlePageChange(pagination.page - 1)
                            }
                            disabled={pagination.page === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() =>
                              handlePageChange(pagination.page + 1)
                            }
                            disabled={pagination.page === pagination.totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal (crear/editar/resetear) */}
      {mostrarModal && (
        <ModalUsuario
          modo={modoModal}
          usuario={usuarioSeleccionado}
          roles={roles || []}
          onClose={() => setMostrarModal(false)}
          onSubmit={handleSubmitModal}
        />
      )}
    </div>
  );
};

// Componente Modal (simplificado por ahora)
interface ModalUsuarioProps {
  modo: 'crear' | 'editar' | 'resetear';
  usuario: UsuarioSinPassword | null;
  roles: any[];
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const ModalUsuario: React.FC<ModalUsuarioProps> = ({
  modo,
  usuario,
  roles,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    nombre: usuario?.nombre || '',
    email: usuario?.email || '',
    password: '',
    rol_id: (usuario?.rol_id || '') as number | '',
    activo: usuario?.activo ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validar que rol_id sea un número válido para crear/editar
    if (modo !== 'resetear' && !formData.rol_id) {
      alert('Por favor seleccione un rol');
      return;
    }

    // Preparar datos para enviar
    const dataToSubmit =
      modo === 'resetear'
        ? { password: formData.password }
        : {
            nombre: formData.nombre,
            email: formData.email,
            rol_id:
              typeof formData.rol_id === 'number'
                ? formData.rol_id
                : parseInt(String(formData.rol_id)),
            ...(modo === 'crear' && { password: formData.password }),
            ...(modo === 'editar' && { activo: formData.activo }),
          };

    onSubmit(dataToSubmit);
  };

  const titulo = {
    crear: 'Nuevo Usuario',
    editar: 'Editar Usuario',
    resetear: 'Resetear Contraseña',
  }[modo];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {titulo}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {modo !== 'resetear' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nombre completo
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, nombre: e.target.value }))
                  }
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rol
                </label>
                <select
                  value={formData.rol_id}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      rol_id: e.target.value ? parseInt(e.target.value) : '',
                    }))
                  }
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Seleccionar rol</option>
                  {roles.map((rol) => (
                    <option key={rol.id} value={rol.id}>
                      {rol.nombre_display}
                    </option>
                  ))}
                </select>
              </div>

              {modo === 'editar' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.activo}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        activo: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                    Usuario activo
                  </label>
                </div>
              )}
            </>
          )}

          {(modo === 'crear' || modo === 'resetear') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {modo === 'crear' ? 'Contraseña' : 'Nueva contraseña'}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, password: e.target.value }))
                }
                required
                minLength={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Mínimo 6 caracteres
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
            >
              {modo === 'crear'
                ? 'Crear'
                : modo === 'editar'
                  ? 'Guardar'
                  : 'Resetear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GestionUsuarios;
